import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { RECITER_FOLDERS, resolveReciter } from "@/data/reciters";
import { API, authFetch } from "@/App";

/**
 * Encapsulates the Reader's audio playback engine:
 *   • single ayah playback with reciter-folder URL resolution
 *   • Bismillah pre-roll for ayah 1 of any surah other than Fatihah / At-Tawba
 *   • normal "repeat each verse" + "repeat set" sequencing
 *   • Stack Mode (1 → 1-2 → 1-2-3 …) with per-stack repeats
 *   • cross-page advance in Mushaf Page Mode (via pageControlsRef)
 *   • Prev/Next ayah navigation
 *   • Stop / pause / resume / start-from-beginning toggle
 *
 * The hook is intentionally *controlled*: the caller owns stack-mode and
 * repeat configuration state and passes either values or refs in. The hook
 * only owns the bits that are pure "audio engine" concerns — the audio
 * element, current index/loop counters, and the playback flags.
 */
export default function useReaderAudio({
  // surrounding state for URL/payload composition
  settings,
  surah,
  surahNumber,
  viewMode,
  playbackQueue,
  // stack-mode state (read + write back to parent)
  stackMode,
  setStackLevel,
  setStackCurrentIndex,
  // refs the parent already maintains
  stackLevelRef,
  stackCurrentIndexRef,
  repeatEachVerseRef,
  repeatSetRef,
  // optional page-mode hooks
  pageControlsRef,
  pendingPageAdvancePlayRef,
  oneshotAudioRef,
}) {
  const audioRef = useRef(null);
  // When we auto-play a Bismillah before ayah 1, we stash the intended
  // (surah, ayah, index) here and let `handleAudioEnded` resume from it.
  const pendingPostBismillahRef = useRef(null);

  const [currentAyah, setCurrentAyah] = useState(1);
  const [currentAyahIndex, setCurrentAyahIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isStopped, setIsStopped] = useState(true);
  const [currentLoop, setCurrentLoop] = useState(0);
  const [currentSetLoop, setCurrentSetLoop] = useState(0);
  const [pageHighlight, setPageHighlight] = useState(null);

  // Internal refs so callbacks always read fresh values without redefining.
  const settingsRef = useRef(settings);
  useEffect(() => { settingsRef.current = settings; }, [settings]);
  const currentLoopRef = useRef(currentLoop);
  const currentSetLoopRef = useRef(currentSetLoop);
  useEffect(() => { currentLoopRef.current = currentLoop; }, [currentLoop]);
  useEffect(() => { currentSetLoopRef.current = currentSetLoop; }, [currentSetLoop]);

  const getAudioUrl = (surahNum, ayahNum) => {
    const id = resolveReciter(settingsRef.current.reciter);
    const folder = RECITER_FOLDERS[id] || "Alafasy_128kbps";
    return `https://everyayah.com/data/${folder}/${String(surahNum).padStart(3, "0")}${String(ayahNum).padStart(3, "0")}.mp3`;
  };

  const queueAyahSurah = (a) => a?.surahNumber ?? parseInt(surahNumber);

  const playAyah = useCallback((surahNum, ayahNum, index = 0) => {
    setCurrentAyah(ayahNum);
    setCurrentAyahIndex(index);
    setIsStopped(false);
    setPageHighlight({ surah: surahNum, ayah: ayahNum });
    if (audioRef.current) {
      const isFirstAyah = ayahNum === 1;
      const needsBismillah =
        isFirstAyah && surahNum !== 1 && surahNum !== 9;

      if (needsBismillah) {
        pendingPostBismillahRef.current = { surahNum, ayahNum, index };
        audioRef.current.src = getAudioUrl(1, 1);
      } else {
        pendingPostBismillahRef.current = null;
        audioRef.current.src = getAudioUrl(surahNum, ayahNum);
      }
      audioRef.current.playbackRate = settings.playbackSpeed;
      audioRef.current.play();
      setIsPlaying(true);
    }
    // Fire-and-forget last-read update for the dashboard "continue" banner.
    authFetch(`${API}/last-read`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        surah_number: surahNum,
        ayah_number: ayahNum,
        surah_name: surah?.englishName || null,
        view_mode: viewMode,
      }),
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.playbackSpeed, surah, viewMode]);

  // Stop audio - resets everything; user can click any ayah to play from there.
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = "";
      pendingPostBismillahRef.current = null;
      setIsPlaying(false);
      setIsStopped(true);
      setCurrentLoop(0);
      setCurrentSetLoop(0);
      if (stackMode) {
        setStackLevel(1);
        setStackCurrentIndex(0);
      }
    }
  };

  // Play/Pause toggle - when stopped, starts from beginning.
  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        if (isStopped || !audioRef.current.src) {
          if (playbackQueue.length > 0) {
            const firstAyah = playbackQueue[0];
            setCurrentLoop(0);
            setCurrentSetLoop(0);
            if (stackMode) {
              setStackLevel(1);
              setStackCurrentIndex(0);
            }
            playAyah(queueAyahSurah(firstAyah), firstAyah.numberInSurah, 0);
          }
        } else {
          audioRef.current.play();
          setIsPlaying(true);
        }
      }
    }
  };

  const handleAudioEnded = useCallback(() => {
    // Bismillah just finished? Resume with the real queued target.
    if (pendingPostBismillahRef.current) {
      const { surahNum, ayahNum, index } = pendingPostBismillahRef.current;
      pendingPostBismillahRef.current = null;
      setCurrentAyah(ayahNum);
      setCurrentAyahIndex(index);
      setPageHighlight({ surah: surahNum, ayah: ayahNum });
      if (audioRef.current) {
        audioRef.current.src = getAudioUrl(surahNum, ayahNum);
        audioRef.current.playbackRate = settingsRef.current.playbackSpeed;
        audioRef.current.play().catch(() => {});
        setIsPlaying(true);
      }
      return;
    }

    const ayahs = playbackQueue;
    if (ayahs.length === 0) return;

    const verseRepeat = repeatEachVerseRef.current;
    const setRepeatCount = repeatSetRef.current;
    const currentSetLoopNum = currentSetLoopRef.current;

    // Stack Mode Logic: Play 1 → 1-2 → 1-2-3, each level repeated by repeatEachVerse.
    if (stackMode) {
      const currentStackLevel = stackLevelRef.current;
      const currentStackIndex = stackCurrentIndexRef.current;
      const currentLoopNum = currentLoopRef.current;
      const maxLoops = verseRepeat === -1 ? Infinity : verseRepeat;

      if (currentStackIndex < currentStackLevel - 1) {
        const nextIndex = currentStackIndex + 1;
        setStackCurrentIndex(nextIndex);
        const nextAyah = ayahs[nextIndex];
        setTimeout(() => {
          playAyah(queueAyahSurah(nextAyah), nextAyah.numberInSurah, nextIndex);
        }, settings.pauseDuration * 1000);
      } else if (currentLoopNum < maxLoops - 1) {
        setCurrentLoop((prev) => prev + 1);
        setStackCurrentIndex(0);
        const firstAyah = ayahs[0];
        setTimeout(() => {
          playAyah(queueAyahSurah(firstAyah), firstAyah.numberInSurah, 0);
        }, settings.pauseDuration * 1000);
      } else if (currentStackLevel < ayahs.length) {
        const newLevel = currentStackLevel + 1;
        setCurrentLoop(0);
        setStackLevel(newLevel);
        setStackCurrentIndex(0);
        const firstAyah = ayahs[0];
        toast.info(`Stack level ${newLevel}: Playing ayahs 1-${newLevel}`);
        setTimeout(() => {
          playAyah(queueAyahSurah(firstAyah), firstAyah.numberInSurah, 0);
        }, settings.pauseDuration * 1000);
      } else {
        const maxSetLoops = setRepeatCount === -1 ? Infinity : setRepeatCount;
        if (currentSetLoopNum < maxSetLoops - 1) {
          setCurrentSetLoop((prev) => prev + 1);
          setCurrentLoop(0);
          setStackLevel(1);
          setStackCurrentIndex(0);
          const firstAyah = ayahs[0];
          toast.info(
            `Set repeat ${currentSetLoopNum + 2}/${setRepeatCount === -1 ? "∞" : setRepeatCount}`
          );
          setTimeout(() => {
            playAyah(queueAyahSurah(firstAyah), firstAyah.numberInSurah, 0);
          }, settings.pauseDuration * 1000);
        } else {
          setIsPlaying(false);
          setIsStopped(true);
          setCurrentLoop(0);
          setCurrentSetLoop(0);
          setStackLevel(1);
          setStackCurrentIndex(0);
          toast.success("Stack mode complete! All ayahs memorized.");
        }
      }
      return;
    }

    // Normal Mode with repeat each verse and repeat set
    const maxVerseLoops = verseRepeat === -1 ? Infinity : verseRepeat;
    const maxSetLoops = setRepeatCount === -1 ? Infinity : setRepeatCount;
    const currentLoopNum = currentLoopRef.current;

    if (currentLoopNum < maxVerseLoops - 1) {
      setCurrentLoop((prev) => prev + 1);
      setTimeout(() => {
        audioRef.current?.play();
      }, settings.pauseDuration * 1000);
    } else if (currentAyahIndex < ayahs.length - 1) {
      setCurrentLoop(0);
      const nextIndex = currentAyahIndex + 1;
      const nextAyah = ayahs[nextIndex];
      setTimeout(() => {
        playAyah(queueAyahSurah(nextAyah), nextAyah.numberInSurah, nextIndex);
      }, settings.pauseDuration * 1000);
    } else if (viewMode === "page" && pageControlsRef?.current?.canGoNext?.()) {
      setCurrentLoop(0);
      if (pendingPageAdvancePlayRef) pendingPageAdvancePlayRef.current = true;
      setTimeout(() => {
        pageControlsRef.current?.goNext?.();
      }, settings.pauseDuration * 1000);
    } else {
      if (currentSetLoopNum < maxSetLoops - 1) {
        setCurrentSetLoop((prev) => prev + 1);
        setCurrentLoop(0);
        setCurrentAyahIndex(0);
        const firstAyah = ayahs[0];
        toast.info(
          `Set repeat ${currentSetLoopNum + 2}/${setRepeatCount === -1 ? "∞" : setRepeatCount}`
        );
        setTimeout(() => {
          playAyah(queueAyahSurah(firstAyah), firstAyah.numberInSurah, 0);
        }, settings.pauseDuration * 1000);
      } else {
        setIsPlaying(false);
        setIsStopped(true);
        setCurrentLoop(0);
        setCurrentSetLoop(0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbackQueue, stackMode, currentAyahIndex, settings.pauseDuration, surahNumber, playAyah, viewMode]);

  const goToPrevAyah = () => {
    if (currentAyahIndex > 0) {
      const prevIndex = currentAyahIndex - 1;
      const prevAyah = playbackQueue[prevIndex];
      playAyah(queueAyahSurah(prevAyah), prevAyah.numberInSurah, prevIndex);
    }
  };

  const goToNextAyah = () => {
    if (currentAyahIndex < playbackQueue.length - 1) {
      const nextIndex = currentAyahIndex + 1;
      const nextAyah = playbackQueue[nextIndex];
      playAyah(queueAyahSurah(nextAyah), nextAyah.numberInSurah, nextIndex);
    }
  };

  // Convenience: stop one-shot audio if the parent uses it on view-mode change.
  const stopOneshot = () => {
    if (oneshotAudioRef?.current) {
      try { oneshotAudioRef.current.pause(); } catch (e) { /* ignore */ }
    }
  };

  return {
    audioRef,
    pendingPostBismillahRef,
    isPlaying,
    setIsPlaying,
    isStopped,
    currentAyah,
    currentAyahIndex,
    setCurrentAyahIndex,
    currentLoop,
    setCurrentLoop,
    currentSetLoop,
    setCurrentSetLoop,
    pageHighlight,
    setPageHighlight,
    getAudioUrl,
    playAyah,
    stopAudio,
    togglePlayPause,
    handleAudioEnded,
    goToPrevAyah,
    goToNextAyah,
    stopOneshot,
  };
}
